import { useState, useMemo } from 'react';
import { useJhaSubmissions, useJhaSubmission } from '../../hooks/useJhaSubmissions.js';
import Modal from '../../components/shared/Modal.jsx';

// 14-day default. Cell = ✓ if a submission exists that day, click opens
// the answers modal pulled fresh from the per-id endpoint (which also
// loads the template version that was active when it was submitted).
export default function JhaSubmissionsPage() {
  const [openId, setOpenId] = useState(null);
  const range = useMemo(() => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const to = +today + 86400000;
    const from = to - 14 * 86400000;
    return { from, to };
  }, []);

  const { data, isLoading } = useJhaSubmissions(range);
  if (isLoading || !data) return <div className="loading">Loading…</div>;

  const dayCols = data.days;

  return (
    <div>
      <div className="range-bar">
        <span style={{ color: 'var(--warm-gray)', fontSize: 11 }}>
          Last 14 days · {data.rows.length} crew
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Crew</th>
              {dayCols.map(d => (
                <th key={d} style={{ fontSize: 10, textAlign: 'center', padding: '6px 4px' }}>
                  {d.slice(5)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => {
              const submittedDates = new Set(r.submissions.map(s => s.submitted_date));
              const submissionByDate = new Map(r.submissions.map(s => [s.submitted_date, s]));
              return (
                <tr key={r.user_id}>
                  <td>{r.user_name}</td>
                  {dayCols.map(d => {
                    const sub = submissionByDate.get(d);
                    const ok = submittedDates.has(d);
                    return (
                      <td
                        key={d}
                        onClick={sub ? () => setOpenId(sub.id) : undefined}
                        style={{
                          textAlign: 'center',
                          color: ok ? 'var(--orange)' : 'var(--text-muted)',
                          fontWeight: ok ? 700 : 400,
                          cursor: ok ? 'pointer' : 'default',
                          padding: '6px 4px',
                        }}
                        title={sub ? new Date(sub.submitted_at).toLocaleString() : 'no submission'}
                      >
                        {ok ? '✓' : '·'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {openId && <SubmissionModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function SubmissionModal({ id, onClose }) {
  const { data, isLoading } = useJhaSubmission(id);
  if (isLoading || !data) {
    return (
      <Modal onClose={onClose} title="JHA submission" icon="ph-shield-check">
        <div className="loading">Loading…</div>
      </Modal>
    );
  }
  const { submission, template, user } = data;
  const answers = submission.answers || {};
  return (
    <Modal
      onClose={onClose}
      title={`${user?.display_name || submission.user_id} — ${submission.submitted_date}`}
      icon="ph-shield-check"
    >
      <div style={{ fontSize: 11, color: 'var(--warm-gray)', marginBottom: 12 }}>
        Submitted {new Date(submission.submitted_at).toLocaleString()} via {submission.source}
        {template && <> · template v{template.version} "{template.name}"</>}
      </div>
      {template?.questions?.map(q => (
        <div key={q.id} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '8px 0', borderBottom: '1px solid var(--surface-container-high)',
        }}>
          <div style={{ fontSize: 13, flex: 1, paddingRight: 12 }}>{q.label}</div>
          <div style={{
            fontFamily: 'var(--font-label)', fontSize: 11, fontWeight: 700,
            color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.06rem',
            whiteSpace: 'pre-wrap', textAlign: 'right',
          }}>
            {formatAnswer(answers[q.id])}
          </div>
        </div>
      ))}
    </Modal>
  );
}

function formatAnswer(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
}
